import React, { useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AutoGrid } from "components/layout/reusableLayouts";
import ReceiptLibraryCard from "components/common/ReceiptLibraryCard";
import {
  Receipt,
  ReceiptLibraryFilter,
  groupReceiptsByCreationDay,
} from "utils/receipts/receiptFormat";
import { useAdminTools } from "hooks/useAdminTools";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import ReceiptLibraryHeading from "./ReceiptLibraryHeading";
import ConfirmReceiptDeleteModal from "./ConfirmReceiptDeleteModal";
import EditReceiptDrawer from "components/common/EditReceiptDrawer";

type ReceiptLibraryGridProps = {
  receipts: Receipt[];
  loadingReceipts: boolean;
  loadingMore: boolean;
  handleScroll: (e: React.UIEvent<HTMLElement>) => void;
  refetchReceipts: () => void;
  handleOpenDetailDrawer: (receiptId: string) => void;
  handleOpenConnectDrawer: (receiptId: string) => void;
  libraryFilter: ReceiptLibraryFilter;
  renderTopFilter?: () => React.ReactNode;
};

const ReceiptLibraryGrid: React.FC<ReceiptLibraryGridProps> = ({
  receipts,
  loadingReceipts,
  loadingMore,
  handleScroll,
  refetchReceipts,
  handleOpenDetailDrawer,
  handleOpenConnectDrawer,
  libraryFilter,
  renderTopFilter,
}) => {
  const { t, i18n } = useTranslation();
  const showUploader = useAdminTools();

  // The card triggers these; the grid owns them, so one modal serves every
  // tile rather than one mounting per card.
  const editDrawer = useDrawerRecord<Receipt>();
  const [receiptPendingDelete, setReceiptPendingDelete] =
    useState<Receipt | null>(null);

  const dateLocale = i18n.language?.toLowerCase().startsWith("es")
    ? "es"
    : "en";

  const dayGroups = useMemo(
    () =>
      groupReceiptsByCreationDay(
        receipts,
        t("ReceiptUnknownCreationDate"),
        dateLocale,
      ),
    [receipts, t, dateLocale],
  );

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <LoadingWithoutModal open={loadingReceipts} contained />
      <Box
        onScroll={handleScroll}
        sx={{
          position: "relative",
          height: "100%",
          width: "100%",
          overflowY: "auto",
          padding: "var(--space-7)",
        }}
      >
        <ReceiptLibraryHeading activeTab={libraryFilter} />
        {renderTopFilter?.()}
        {!loadingReceipts && receipts.length === 0 ? (
          <Typography
            variant="body2"
            component="p"
            sx={{
              color: "var(--color-text-tertiary)",
              paddingY: "var(--space-9)",
              textAlign: "center",
            }}
          >
            {t("No receipts found")}
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-10)",
              width: "100%",
            }}
          >
            {dayGroups.map((group) => (
              <Box
                key={group.dayKey}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-7)",
                  width: "100%",
                }}
              >
                <Typography variant="h5" component="span">
                  {group.label}
                </Typography>
                <AutoGrid min="220px" gap="var(--space-4)">
                  {group.receipts.map((receipt) => (
                    <ReceiptLibraryCard
                      key={receipt.id}
                      receipt={receipt}
                      showUploader={showUploader}
                      onOpen={() => handleOpenDetailDrawer(receipt.id)}
                      onConnect={() => handleOpenConnectDrawer(receipt.id)}
                      onEdit={() => editDrawer.show(receipt)}
                      onDelete={() => setReceiptPendingDelete(receipt)}
                    />
                  ))}
                </AutoGrid>
              </Box>
            ))}
          </Box>
        )}
        {loadingMore && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              paddingY: "var(--space-4)",
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>

      {editDrawer.record && (
        <EditReceiptDrawer
          open={editDrawer.open}
          onClose={editDrawer.close}
          onAfterClose={editDrawer.afterClose}
          receipt={editDrawer.record}
          showUploader={showUploader}
          onUpdated={() => {
            refetchReceipts();
            editDrawer.close();
          }}
        />
      )}

      <ConfirmReceiptDeleteModal
        open={receiptPendingDelete !== null}
        handleCloseModal={() => setReceiptPendingDelete(null)}
        receiptId={receiptPendingDelete?.id ?? ""}
        refetchReceipts={refetchReceipts}
      />
    </Box>
  );
};

export default ReceiptLibraryGrid;
